import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { get } from "../lib/http";
import { API } from "../config/api";

export default function Hero({ autoPlayDelay = 2000, transitionMs = 600 }) {
  const [slides, setSlides] = useState([]);
  const [cur, setCur] = useState(1);        // index within cloned track (start at 1 = first real)
  const [paused, setPaused] = useState(false);
  const [anim, setAnim] = useState(true);

  // measurements
  const [positions, setPositions] = useState([]); // left offsets within track for cloned slides
  const [trackWidth, setTrackWidth] = useState(0);

  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const autoplayRef = useRef(null);

  // ---------- Fetch banners ----------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(API.banner.list, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });

        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.items)
              ? res.items
              : [];

        const active = list.filter((b) => b?.isActive === true && b?.imageUrl);
        if (mounted) {
          setSlides(active);
          setCur(active.length > 1 ? 1 : 0); // if only 1, just sit at 0
        }
      } catch (e) {
        console.error("getAllBanners error:", e);
      }
    })();
    return () => (mounted = false);
  }, []);

  // ---------- Build cloned render list ----------
  const renderSlides = useMemo(() => {
    if (slides.length === 0) return [];
    if (slides.length === 1) return [slides[0]]; // single: no need for clones
    return [slides[slides.length - 1], ...slides, slides[0]];
  }, [slides]);

  // reset refs array length when renderSlides changes
  useEffect(() => {
    itemRefs.current = Array(renderSlides.length).fill(null);
  }, [renderSlides.length]);

  // ---------- Measure positions (pixel-perfect centering) ----------
  const measure = () => {
    if (!trackRef.current || !wrapRef.current) return;
    const nodes = itemRefs.current.filter(Boolean);
    if (nodes.length === 0) return;

    const newPositions = nodes.map((n) => n.offsetLeft);
    setPositions(newPositions);
    setTrackWidth(trackRef.current.scrollWidth);
  };

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (trackRef.current) ro.observe(trackRef.current);
    itemRefs.current.forEach((n) => n && ro.observe(n));
    return () => ro.disconnect();
    // re-measure whenever list or index changes (layout can change with scaling)
  }, [renderSlides.length, cur]);

  // ---------- Autoplay ----------
  useEffect(() => {
    if (paused) return;
    if (renderSlides.length <= 1) return;
    clearTimeout(autoplayRef.current);
    autoplayRef.current = setTimeout(() => {
      next();
    }, autoPlayDelay);
    return () => clearTimeout(autoplayRef.current);
  }, [cur, paused, renderSlides.length, autoPlayDelay]);

  // ---------- Controls ----------
  const next = () => {
    if (renderSlides.length <= 1) return;
    setAnim(true);
    setCur((i) => i + 1);
  };
  const prev = () => {
    if (renderSlides.length <= 1) return;
    setAnim(true);
    setCur((i) => i - 1);
  };

  // seamless loop: jump from clones to real without animation
  const handleTransitionEnd = () => {
    if (slides.length <= 1) return;
    const lastRealIdx = slides.length; // within cloned list, real slides are [1..slides.length]
    if (cur === 0) {
      // we moved onto the leading clone (last slide clone) -> jump to last real
      setAnim(false);
      setCur(lastRealIdx);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnim(true))
      );
    } else if (cur === lastRealIdx + 1) {
      // we moved onto the trailing clone (first slide clone) -> jump to first real
      setAnim(false);
      setCur(1);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnim(true))
      );
    }
  };

  // ---------- Swipe ----------
  const startX = useRef(0);
  const deltaX = useRef(0);
  const onTouchStart = (e) => {
    setPaused(true);
    startX.current = e.touches[0].clientX;
    deltaX.current = 0;
  };
  const onTouchMove = (e) => {
    deltaX.current = e.touches[0].clientX - startX.current;
  };
  const onTouchEnd = () => {
    const threshold = 50;
    if (deltaX.current < -threshold) next();
    else if (deltaX.current > threshold) prev();
    setPaused(false);
  };

  // ---------- Translate (center current card) ----------
  const translateX = useMemo(() => {
    if (renderSlides.length === 0) return 0;
    const viewport = wrapRef.current?.clientWidth ?? 0;
    const card = itemRefs.current[cur]?.clientWidth ?? 0;

    if (viewport <= 767) {
      // mobile: snap to card start
      return positions[cur] ?? 0;
    }

    // center the current card
    const cardCenter = (positions[cur] ?? 0) + card / 2;
    const viewCenter = viewport / 2;
    const target = Math.max(0, cardCenter - viewCenter);

    const maxScroll = Math.max(0, trackWidth - viewport);
    return Math.min(target, maxScroll);
  }, [positions, cur, trackWidth, renderSlides.length]);

  // map real active dot index
  const activeDot =
    slides.length > 1 ? ((cur - 1 + slides.length) % slides.length) : 0;

  if (renderSlides.length === 0) {
    return (
      <div className="h-[180px] sm:h-[220px] md:h-[260px] lg:h-[300px] flex items-center justify-center bg-gray-200 rounded-2xl text-sm text-slate-600">
        Loading banners...
      </div>
    );
  }

  return (
    <section
      className="relative overflow-x-clip"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div ref={wrapRef} className="overflow-hidden">
        <div
          ref={trackRef}
          className="flex items-stretch gap-2 md:gap-3 lg:gap-4 transition-transform ease-in-out"
          style={{
            transform: `translateX(-${translateX}px)`,
            transitionDuration: anim ? `${transitionMs}ms` : "0ms",
            willChange: "transform",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {renderSlides.map((s, i) => (
            <div
              key={`hero-${s?._id ?? "banner"}-${i}`}
              ref={(el) => (itemRefs.current[i] = el)}
              className={`
                relative flex-shrink-0
                w-[100%] md:w-[70%] lg:w-[70%]
                transition-transform duration-500
                ${i === cur ? "scale-[1.00]" : "scale-[1.00]"}
              `}
            >
              <div className="relative h-[200px] sm:h-[220px] md:h-[260px] lg:h-[280px] rounded-xl md:rounded-2xl overflow-hidden cursor-pointer">
                {/* Mobile image */}
                <img
                  src={s?.mobileImageUrl || s?.imageUrl}
                  alt={s?.title ?? "Banner"}
                  draggable={false}
                  className="absolute top-0 left-0 w-full h-full object-fill rounded-2xl block md:hidden"
                  style={{ objectPosition: "center", backgroundColor: "#000" }}
                />

                {/* Desktop image */}
                <img
                  src={s?.imageUrl || s?.mobileImageUrl}
                  alt={s?.title ?? "Banner"}
                  draggable={false}
                  className="absolute top-0 left-0 w-full h-full object-fill rounded-2xl hidden md:block"
                  style={{ objectPosition: "center", backgroundColor: "#000" }}
                />
              </div>

            </div>
          ))}
        </div>

        {/* Prev / Next */}
        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full p-2 text-white focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Previous"
            >
              ◀
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full p-2 text-white focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Next"
            >
              ▶
            </button>
          </>
        )}

        {/* Dots for REAL slides */}
        {slides.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {slides.map((_, d) => (
              <button
                key={`dot-${d}`}
                onClick={() => {
                  setAnim(true);
                  setCur(d + 1); // dot maps to real index -> shift by +1 because of leading clone
                }}
                className={`w-2.5 h-2.5 rounded-full transition 
                  ${activeDot === d ? "bg-white" : "bg-white/40"}`}
                aria-label={`Go to slide ${d + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
